import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Button,
  IconButton,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
  Groups as GroupsIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import api, { disciplinesApi } from '../../services/api';

// Accent color per asset category, for quick visual distinction of the flow cards.
const CATEGORY_COLORS = {
  'Road Pavement': '#1976d2',
  'Road Furniture': '#f57c00',
  'Geotechnical': '#2e7d32',
  'Structural': '#6a1b9a',
  'Vertical Asset': '#7c4dff',
};

/**
 * Inspection Flow admin page (manager/admin).
 *
 * A flow is defined per asset category (one flow per category). It routes an
 * AI-analysed inspection through two team steps:
 *   Step 1 — team notified to verify the AI analysis & recommendation
 *   Step 2 — team that reviews/validates step 1's work
 * Step teams are scoped to the flow's asset-category discipline. Backend: /api/inspection-flows.
 */
const InspectionFlowPage = () => {
  const theme = useTheme();
  const [flows, setFlows] = useState([]);
  const [teams, setTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create/edit dialog
  const [open, setOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [assetCategoryId, setAssetCategoryId] = useState('');
  const [step1TeamId, setStep1TeamId] = useState('');
  const [step2TeamId, setStep2TeamId] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [flowsRes, teamsRes, catsRes] = await Promise.all([
        api.get('/inspection-flows'),
        api.get('/teams/details'),
        disciplinesApi.getDisciplines(),
      ]);
      setFlows(flowsRes.data || []);
      setTeams(teamsRes.data || []);
      setCategories(Array.isArray(catsRes.data) ? catsRes.data : []);
    } catch (err) {
      setError('Failed to load inspection flows');
    } finally {
      setLoading(false);
    }
  };

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // Categories that don't yet have a flow (only these can be picked when creating).
  const usedCategoryIds = new Set(flows.map((f) => f.asset_category_id));
  const availableCategories = categories.filter((c) => !usedCategoryIds.has(c.id));

  // Teams scoped to the currently-selected category's discipline.
  const matchingTeams = assetCategoryId
    ? teams.filter((t) => Number(t.discipline_id) === Number(assetCategoryId))
    : [];

  const handleOpenCreate = () => {
    setEditingFlow(null);
    setAssetCategoryId('');
    setStep1TeamId('');
    setStep2TeamId('');
    setOpen(true);
  };

  const handleOpenEdit = (flow) => {
    setEditingFlow(flow);
    setAssetCategoryId(flow.asset_category_id);
    setStep1TeamId(flow.step1_team_id || '');
    setStep2TeamId(flow.step2_team_id || '');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingFlow(null);
    setAssetCategoryId('');
    setStep1TeamId('');
    setStep2TeamId('');
  };

  // Category drives the team options, so reset the team picks when it changes.
  const handleCategoryChange = (value) => {
    setAssetCategoryId(value);
    setStep1TeamId('');
    setStep2TeamId('');
  };

  const canSave = !!assetCategoryId && !!step1TeamId && !!step2TeamId;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      if (editingFlow) {
        await api.put(`/inspection-flows/${editingFlow.id}`, {
          step1_team_id: step1TeamId,
          step2_team_id: step2TeamId,
        });
      } else {
        await api.post('/inspection-flows', {
          asset_category_id: assetCategoryId,
          step1_team_id: step1TeamId,
          step2_team_id: step2TeamId,
        });
      }
      handleClose();
      fetchAll();
      notify(editingFlow ? 'Inspection flow updated' : 'Inspection flow created');
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to save inspection flow', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/inspection-flows/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchAll();
      notify('Inspection flow deleted');
    } catch (err) {
      notify('Failed to delete inspection flow', 'error');
    }
  };

  const getCategoryColor = (name) => CATEGORY_COLORS[name] || theme.palette.primary.main;

  const renderStep = (num, sublabel, teamName, color) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1.5,
        borderRadius: 2,
        backgroundColor: alpha(color, 0.06),
        border: `1px solid ${alpha(color, 0.18)}`,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 22,
            height: 22,
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: color,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {num}
        </Box>
        <Typography variant="caption" fontWeight={600} color="text.secondary" noWrap>
          {sublabel}
        </Typography>
      </Stack>
      {teamName ? (
        <Chip
          icon={<GroupsIcon />}
          label={teamName}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 600, borderColor: alpha(color, 0.5), color, pl: 0.5, '& .MuiChip-icon': { color, marginLeft: '8px' } }}
        />
      ) : (
        <Chip
          icon={<WarningIcon />}
          label="Team removed — reassign"
          size="small"
          color="error"
          variant="outlined"
          sx={{ pl: 0.5, '& .MuiChip-icon': { marginLeft: '8px' } }}
        />
      )}
    </Box>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading inspection flows...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <AccountTreeIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>Inspection Flow</Typography>
            <Typography variant="body2" color="text.secondary">
              Define a two-step approval flow per asset category for AI-analysed inspections
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          disabled={availableCategories.length === 0}
        >
          Create Inspection Flow
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {availableCategories.length === 0 && flows.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Every asset category already has a flow. Edit or delete an existing one to make changes.
        </Alert>
      )}

      {/* Flows list */}
      {flows.length === 0 ? (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              backgroundColor: alpha(theme.palette.grey[100], 0.4),
              borderRadius: 2,
              border: `2px dashed ${alpha(theme.palette.grey[400], 0.5)}`,
            }}
          >
            <AccountTreeIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>No inspection flows yet</Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
              Create a flow to route an asset category's AI-analysed inspections through two team steps
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} disabled={availableCategories.length === 0}>
              Create First Flow
            </Button>
          </Box>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          {flows.map((flow) => {
            const color = getCategoryColor(flow.asset_category_name);
            return (
            <Card
              key={flow.id}
              sx={{
                borderRadius: 3,
                boxShadow: theme.shadows[2],
                borderLeft: `4px solid ${color}`,
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow .2s, transform .2s',
                '&:hover': { boxShadow: theme.shadows[5], transform: 'translateY(-2px)' },
              }}
            >
              <CardHeader
                avatar={
                  <Box sx={{ backgroundColor: alpha(color, 0.12), borderRadius: '50%', p: 1, display: 'flex' }}>
                    <AccountTreeIcon sx={{ color, fontSize: 20 }} />
                  </Box>
                }
                title={<Typography variant="h6" fontWeight={600}>{flow.asset_category_name}</Typography>}
                subheader={<Typography variant="caption" color="text.secondary">2-step approval</Typography>}
                action={
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit Flow">
                      <IconButton size="small" onClick={() => handleOpenEdit(flow)} sx={{ color: 'primary.main' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Flow">
                      <IconButton size="small" onClick={() => setDeleteTarget(flow)} sx={{ color: 'error.main' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Stack direction="row" spacing={1} alignItems="stretch">
                  {renderStep(1, 'Verify & recommend', flow.step1_team_name, color)}
                  <Stack justifyContent="center" sx={{ flexShrink: 0 }}>
                    <ArrowForwardIcon sx={{ color: alpha(color, 0.7) }} />
                  </Stack>
                  {renderStep(2, 'Review & validate', flow.step2_team_name, color)}
                </Stack>
              </CardContent>
            </Card>
            );
          })}
        </Box>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AccountTreeIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {editingFlow ? 'Edit Inspection Flow' : 'Create Inspection Flow'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth required disabled={!!editingFlow}>
              <InputLabel>Asset Category</InputLabel>
              <Select
                value={assetCategoryId}
                label="Asset Category"
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                {editingFlow ? (
                  <MenuItem value={editingFlow.asset_category_id}>{editingFlow.asset_category_name}</MenuItem>
                ) : availableCategories.length === 0 ? (
                  <MenuItem value="" disabled>All categories already have a flow</MenuItem>
                ) : (
                  availableCategories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)
                )}
              </Select>
            </FormControl>

            {assetCategoryId && matchingTeams.length === 0 && (
              <Alert severity="warning">
                No teams exist for this category's discipline yet. Create one in User Management → Teams first.
              </Alert>
            )}

            <FormControl fullWidth required disabled={!assetCategoryId || matchingTeams.length === 0}>
              <InputLabel>Step 1 Team (Verify &amp; recommend)</InputLabel>
              <Select
                value={step1TeamId}
                label="Step 1 Team (Verify & recommend)"
                onChange={(e) => setStep1TeamId(e.target.value)}
              >
                {matchingTeams.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl fullWidth required disabled={!assetCategoryId || matchingTeams.length === 0}>
              <InputLabel>Step 2 Team (review &amp; validate)</InputLabel>
              <Select
                value={step2TeamId}
                label="Step 2 Team (review & validate)"
                onChange={(e) => setStep2TeamId(e.target.value)}
              >
                {matchingTeams.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>

            <Typography variant="caption" color="text.secondary">
              Teams are scoped to the selected category's discipline. Step 1 is notified when an inspection is
              AI-analysed; Step 2 reviews and validates Step 1's work.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!canSave || saving}>
            {saving ? 'Saving...' : editingFlow ? 'Update Flow' : 'Create Flow'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <WarningIcon color="error" />
            <Typography variant="h6">Delete Inspection Flow</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete the inspection flow for <strong>{deleteTarget?.asset_category_name}</strong>? This removes its
            two-step routing. You can recreate it later.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
            Delete Flow
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default InspectionFlowPage;
